import type React from 'react';
import type { QuickFillItem } from '../../../shared/types';
import type { FilterableTable, TableInitialPreset } from '../../../shared/ui/table';

export interface QuickFillSelectedProperty {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  checked: boolean;
  revitParameter?: string;
  isReadOnly?: boolean;
  isInstance?: boolean;
}

export interface QuickFillTableCellSelectionHandlers {
  handleCellClick: (
    cell: { rowIndex: number; columnLabel: string },
    event: React.MouseEvent
  ) => void;
  handleCellMouseDown: (
    cell: { rowIndex: number; columnLabel: string },
    event: React.MouseEvent
  ) => void;
  handleCellMouseEnter: (
    cell: { rowIndex: number; columnLabel: string },
    event: React.MouseEvent
  ) => void;
  handleCellMouseUp: () => void;
  consumePendingCellClick: () => boolean;
  isCellSelected: (rowIndex: number, columnLabel: string) => boolean;
}

export interface QuickFillTableRowSelectionHandlers {
  handleSelectRow: (index: number) => void;
  handleSelectAllRows: (rowIndices: number[]) => void;
}

export interface QuickFillTableViewportHandlers {
  handleFilteredRowIndicesChange: (rowIndices: number[]) => void;
  handleViewportRangeChange: (
    range: {
      startPosition: number | null;
      endPosition: number | null;
      totalFilteredRows: number;
    }
  ) => void;
}

export interface BuildQuickFillTablePropsInputs {
  quickFillData: QuickFillItem[];
  nameColumnLabel: string;
  selectedRows: number[];
  editedCells: Record<string, boolean>;
  failedCellErrors: Record<string, string>;
  validationFocusPreset: TableInitialPreset | null;
  validationFocusColumnKeys: (keyof QuickFillItem)[];
  cellSelection: QuickFillTableCellSelectionHandlers;
  rowSelection: QuickFillTableRowSelectionHandlers;
  viewport: QuickFillTableViewportHandlers;
  tableViewportRef: React.Ref<HTMLDivElement>;
  filterTriggerRef: React.RefObject<HTMLElement | null>;
  filterPopoverRef: React.RefObject<HTMLDivElement | null>;
  selectedProperties: QuickFillSelectedProperty[];
  onRowChange: (index: number, field: keyof QuickFillItem, value: string) => void;
  navigationMarkerRows: number[];
  navigationRequest: {
    rowIndex: number;
    requestId: number;
    columnLabel?: string;
  } | null;
  activeFindReplaceMatch: { row: number; column: string } | null;
  isLoading: boolean;
  loadingText: string;
  previewFilterColumn: string | null;
  showTourFilterPreview: boolean;
}

export type BuildQuickFillTablePropsResult = React.ComponentProps<typeof FilterableTable>;

/**
 * Pure builder for the FilterableTable props used by QuickFillPage.
 *
 * No hooks, no JSX — just maps the page-level state and handlers into the
 * shape expected by FilterableTable.
 */
export function buildQuickFillTableProps(
  inputs: BuildQuickFillTablePropsInputs
): BuildQuickFillTablePropsResult {
  const {
    quickFillData,
    selectedProperties,
    nameColumnLabel,
    selectedRows,
    editedCells,
    failedCellErrors,
    validationFocusPreset,
    validationFocusColumnKeys,
    cellSelection,
    rowSelection,
    viewport,
    tableViewportRef,
    filterTriggerRef,
    filterPopoverRef,
    navigationMarkerRows,
    navigationRequest,
    activeFindReplaceMatch,
    onRowChange,
    isLoading,
    loadingText,
    previewFilterColumn,
    showTourFilterPreview,
  } = inputs;

  return {
    data: quickFillData,
    onRowChange,
    nameColumnLabel,
    selectedProperties,
    externalEditedCells: editedCells,
    selectedRows,
    onSelectRow: rowSelection.handleSelectRow,
    onSelectAllRows: rowSelection.handleSelectAllRows,
    onCellClick: cellSelection.handleCellClick,
    onCellMouseDown: cellSelection.handleCellMouseDown,
    onCellMouseEnter: cellSelection.handleCellMouseEnter,
    onCellMouseUp: cellSelection.handleCellMouseUp,
    consumePendingCellClick: cellSelection.consumePendingCellClick,
    isCellSelected: cellSelection.isCellSelected,
    failedCellErrors,
    isLoading,
    markedRowIndices: navigationMarkerRows,
    navigationRequest,
    activeFindReplaceMatch,
    highlightedColumns: validationFocusColumnKeys,
    initialPreset: validationFocusPreset,
    onVisibleRowIndicesChange: viewport.handleFilteredRowIndicesChange,
    onViewportRangeChange: viewport.handleViewportRangeChange,
    tableViewportRef,
    tourPreviewFilter: previewFilterColumn
      ? {
          column: previewFilterColumn as keyof QuickFillItem,
          open: showTourFilterPreview,
          interactive: showTourFilterPreview,
          triggerRef: filterTriggerRef,
          popoverRef: filterPopoverRef,
        }
      : null,
    loadingText,
  };
}
